<template>
  <div class="tw:flex tw:items-center tw:gap-6 tw:justify-end">
    <a-popover
      trigger="click"
      placement="bottomLeft"
      v-model:open="visible"
      :overlay-inner-style="{ padding: '0px' }"
      :overlay-style="{ zIndex: 1100 }"
      :get-popup-container="getPopupContainer"
      overlay-class-name="mars-color-picker-popover"
      class="tw:bg-s-d-s-dark-background-surface-secondary"
    >
      <template #default>
        <div
          class="tw:w-[16px] tw:h-[16px] tw:cursor-pointer"
          :style="{ backgroundColor: `${currentValue} !important` }"
        ></div>
      </template>
      <template #content>
        <div class="tw:p-12">
          <div class="tw:flex tw:items-center tw:justify-between tw:mb-12">
            <div class="tw:text-2 tw:text-white tw:font-poppins">Set Orbital Track Colour</div>
            <div class="tw:cursor-pointer" @mousedown.stop.prevent="handleClose">
              <IconBase variant="subtle">
                <template #icon>
                  <IconClose width="16" height="16" />
                </template>
              </IconBase>
            </div>
          </div>
          <v-color-picker
            v-model="vColorPickerValue"
            hide-inputs
            @click.stop
            @update:model-value="handleVColorPickerChange"
          ></v-color-picker>
          <div class="tw:flex tw:gap-6 tw:mt-12">
            <div class="tw:w-[100px]">
              <div class="tw:text-2 tw:text-white tw:font-poppins">Hex</div>
              <InputBaseAntd
                v-model="hexInput"
                class="tw:mt-8 tw:bg-s-d-s-dark-background-surface-tertiary tw:w-full"
                borderless
                @change="handleHexChange"
                @blur="handleHexBlur"
              />
            </div>
            <div class="tw:w-[62px]">
              <div class="tw:text-2 tw:text-white tw:font-poppins">R</div>
              <InputNumberBaseAntd
                v-model="rInputNumber"
                class="tw:mt-8 tw:bg-s-d-s-dark-background-surface-tertiary tw:w-full"
                :min="0"
                :max="255"
                borderless
                @change="handleRgbChange"
                @blur="handleRgbBlur"
              />
            </div>
            <div class="tw:w-[62px]">
              <div class="tw:text-2 tw:text-white tw:font-poppins">G</div>
              <InputNumberBaseAntd
                v-model="gInputNumber"
                class="tw:mt-8 tw:bg-s-d-s-dark-background-surface-tertiary tw:w-full"
                :min="0"
                :max="255"
                borderless
                @change="handleRgbChange"
                @blur="handleRgbBlur"
              />
            </div>
            <div class="tw:w-[62px]">
              <div class="tw:text-2 tw:text-white tw:font-poppins">B</div>
              <InputNumberBaseAntd
                v-model="bInputNumber"
                class="tw:mt-8 tw:bg-s-d-s-dark-background-surface-tertiary tw:w-full"
                :min="0"
                :max="255"
                borderless
                @change="handleRgbChange"
                @blur="handleRgbBlur"
              />
            </div>
          </div>
        </div>
      </template>
    </a-popover>
    <label class="tw:text-2 tw:text-white tw:font-poppins">{{ displayColorHex }}</label>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import './mars-color-picker.less'
import IconBase from '@/components/base/IconBase.vue'
import IconClose from '@/components/icons.new/IconClose.vue'
import InputBaseAntd from '@/components/base/antd/InputBaseAntd.vue'
import InputNumberBaseAntd from '@/components/base/antd/InputNumberBaseAntd.vue'

interface ColorObject {
  rgba: {
    r: number
    g: number
    b: number
    a: number
  }
  hex?: string
}

interface Props {
  value?: string
  modelValue?: string
  hiddenAlpha?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  value: 'rgba(255,255,255,1)',
  modelValue: undefined,
  hiddenAlpha: false,
})

const emit = defineEmits<{
  'update:value': [value: string]
  'update:modelValue': [value: string]
  change: [color: ColorObject | null]
  close: []
}>()

// Support both v-model (modelValue) and value prop for backward compatibility
const currentValue = computed(() => props.modelValue ?? props.value ?? 'rgba(255,255,255,1)')

// Convert rgba/rgb to hex for display
const rgbaToHex = (color: string): string => {
  if (!color || typeof color !== 'string') {
    return '#FFFFFF'
  }

  // If already hex, return uppercase
  if (color.startsWith('#')) {
    return color.toUpperCase()
  }

  // Support both rgb() and rgba() formats
  const match = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\)/)
  if (match) {
    const r = parseInt(match[1], 10)
    const g = parseInt(match[2], 10)
    const b = parseInt(match[3], 10)
    const hex = ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()
    return `#${hex}`
  }

  return '#FFFFFF'
}

// Display color in hex format for label
const displayColorHex = computed(() => rgbaToHex(currentValue.value))

const visible = ref(false)
const pointColor = ref(currentValue.value || '')
const colorObject = ref<ColorObject | null>(null)

/**
 * Where to teleport the popover's floating panel.
 *
 * Ant Design Vue's default (`document.body`) makes the panel a sibling of
 * Vuetify's own overlay portal (`.v-overlay-container`), not a descendant of
 * it. That defeats a plain z-index bump: bumping the popover's OWN z-index
 * (still done above, for the non-dialog fallback below) only wins the
 * comparison if something in its ancestor chain actually participates in
 * the outer stacking context at that level -- but the popover's antd-owned
 * wrapper is an unstyled, zero-size `position: static` box (its only child
 * is itself absolutely positioned, so it contributes no layout size), so
 * raising ITS z-index changes nothing either. Cross-portal z-index tuning
 * between two independent teleport roots is fragile by construction.
 *
 * The robust fix used everywhere else in this app for a floating panel that
 * must render above a Vuetify dialog: teleport it INTO that dialog's own
 * overlay content element instead of past it. As a genuine DOM descendant
 * of `.v-overlay__content`, the panel is stacked by ordinary paint order,
 * not by a second z-index negotiation -- and it stays a sibling of (not a
 * descendant inside) any scrollable content in that dialog, so it is not
 * clipped by the form's own overflow.
 *
 * Falls back to `document.body` (antd's own default) when there is no
 * enclosing Vuetify overlay -- e.g. GraphicOptionsPanel, which floats
 * directly over the map with no dialog in front of it.
 */
function getPopupContainer(triggerNode: HTMLElement): HTMLElement {
  return triggerNode.closest<HTMLElement>('.v-overlay__content') ?? document.body
}

// Method to open popover programmatically
const openPopover = () => {
  visible.value = true
}

defineExpose({
  openPopover,
})

// Input refs
const hexInput = ref('')
const rInput = ref('')
const gInput = ref('')
const bInput = ref('')

// Computed properties for number inputs (R, G, B)
const rInputNumber = computed({
  get: () => {
    const num = parseInt(rInput.value) || 0
    return Math.max(0, Math.min(255, num))
  },
  set: (val: number) => {
    rInput.value = val.toString()
  },
})

const gInputNumber = computed({
  get: () => {
    const num = parseInt(gInput.value) || 0
    return Math.max(0, Math.min(255, num))
  },
  set: (val: number) => {
    gInput.value = val.toString()
  },
})

const bInputNumber = computed({
  get: () => {
    const num = parseInt(bInput.value) || 0
    return Math.max(0, Math.min(255, num))
  },
  set: (val: number) => {
    bInput.value = val.toString()
  },
})

// Vuetify color picker uses hex format
const vColorPickerValue = computed({
  get: () => {
    return rgbaToHex(pointColor.value)
  },
  set: (val: string) => {
    // Vuetify returns hex format
    if (val && val.startsWith('#')) {
      const hex = val.replace('#', '')
      const r = parseInt(hex.substring(0, 2), 16)
      const g = parseInt(hex.substring(2, 4), 16)
      const b = parseInt(hex.substring(4, 6), 16)
      const a = colorObject.value?.rgba.a ?? 1
      updateColorFromRgb(r, g, b, a)
    }
  },
})

// Parse color to get RGB values
const parseColor = (color: string): { r: number; g: number; b: number; a: number } => {
  if (!color || typeof color !== 'string') {
    return { r: 255, g: 255, b: 255, a: 1 }
  }

  // Hex format
  if (color.startsWith('#')) {
    const hex = color.replace('#', '')
    const r = parseInt(hex.substring(0, 2), 16) || 255
    const g = parseInt(hex.substring(2, 4), 16) || 255
    const b = parseInt(hex.substring(4, 6), 16) || 255
    const a = hex.length === 8 ? parseInt(hex.substring(6, 8), 16) / 255 : 1
    return { r, g, b, a }
  }

  // RGB/RGBA format
  const match = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\)/)
  if (match) {
    return {
      r: parseInt(match[1], 10),
      g: parseInt(match[2], 10),
      b: parseInt(match[3], 10),
      a: match[4] ? parseFloat(match[4]) : 1,
    }
  }

  return { r: 255, g: 255, b: 255, a: 1 }
}

// Update inputs from current color
const updateInputs = () => {
  const { r, g, b } = parseColor(pointColor.value)
  hexInput.value = rgbaToHex(pointColor.value).toUpperCase()
  rInput.value = r.toString()
  gInput.value = g.toString()
  bInput.value = b.toString()
}

// Update color from RGB values
const updateColorFromRgb = (r: number, g: number, b: number, a: number = 1) => {
  const clampedR = Math.max(0, Math.min(255, r))
  const clampedG = Math.max(0, Math.min(255, g))
  const clampedB = Math.max(0, Math.min(255, b))
  const clampedA = Math.max(0, Math.min(1, a))

  pointColor.value = `rgba(${clampedR},${clampedG},${clampedB},${clampedA})`
  colorObject.value = {
    rgba: {
      r: clampedR,
      g: clampedG,
      b: clampedB,
      a: clampedA,
    },
    hex: rgbaToHex(pointColor.value),
  }
  emitChangeEvent()
  updateInputs()
}

// Handle v-color-picker change
const handleVColorPickerChange = (val: string | { hex?: string; alpha?: number }) => {
  // Vuetify color picker returns an object with hex property
  if (val && typeof val === 'object' && val.hex) {
    const hex = val.hex.replace('#', '')
    const r = parseInt(hex.substring(0, 2), 16)
    const g = parseInt(hex.substring(2, 4), 16)
    const b = parseInt(hex.substring(4, 6), 16)
    const a = val.alpha !== undefined ? val.alpha : (colorObject.value?.rgba.a ?? 1)
    updateColorFromRgb(r, g, b, a)
  } else if (typeof val === 'string' && val.startsWith('#')) {
    vColorPickerValue.value = val
  }
}

// Handle hex input change
const handleHexChange = () => {
  const hex = hexInput.value.replace('#', '').trim()
  if (hex.length === 6 && /^[0-9A-Fa-f]{6}$/.test(hex)) {
    const r = parseInt(hex.substring(0, 2), 16)
    const g = parseInt(hex.substring(2, 4), 16)
    const b = parseInt(hex.substring(4, 6), 16)
    const a = colorObject.value?.rgba.a ?? 1
    updateColorFromRgb(r, g, b, a)
  }
}

// Handle hex input blur - validate and format
const handleHexBlur = () => {
  let hex = hexInput.value.replace('#', '').trim().toUpperCase()
  if (hex.length === 3) {
    // Expand shorthand hex (e.g., FFF -> FFFFFF)
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('')
  }
  if (hex.length === 6 && /^[0-9A-F]{6}$/.test(hex)) {
    hexInput.value = `#${hex}`
    handleHexChange()
  } else {
    // Reset to current value if invalid
    updateInputs()
  }
}

// Handle RGB input change
const handleRgbChange = () => {
  const r = parseInt(rInput.value) || 0
  const g = parseInt(gInput.value) || 0
  const b = parseInt(bInput.value) || 0
  const a = colorObject.value?.rgba.a ?? 1
  updateColorFromRgb(r, g, b, a)
}

// Handle RGB input blur - clamp values
const handleRgbBlur = () => {
  const r = Math.max(0, Math.min(255, parseInt(rInput.value) || 0))
  const g = Math.max(0, Math.min(255, parseInt(gInput.value) || 0))
  const b = Math.max(0, Math.min(255, parseInt(bInput.value) || 0))
  const a = colorObject.value?.rgba.a ?? 1
  updateColorFromRgb(r, g, b, a)
}

let timeTik: ReturnType<typeof setTimeout> | undefined

// Watch for external value changes
watch(currentValue, (newValue) => {
  if (newValue !== pointColor.value) {
    pointColor.value = newValue || ''
    updateInputs()
  }
})

// Initialize inputs when popover opens
watch(visible, (isOpen) => {
  if (isOpen) {
    updateInputs()
  }
})

const handleClose = () => {
  setTimeout(() => {
    visible.value = false
  }, 0)
  emit('close')
}

const emitChangeEvent = () => {
  if (timeTik) {
    clearTimeout(timeTik)
  }
  timeTik = setTimeout(() => {
    emit('update:value', pointColor.value)
    emit('update:modelValue', pointColor.value)
    emit('change', colorObject.value)
  }, 50)
}
</script>

<style scoped lang="less">
:global(.mars-color-picker-popover) {
  // StyleDialog is a Vuetify overlay at z-index 100. Ant Design popovers are
  // teleported outside that overlay, so the picker needs its own explicit
  // stacking level instead of inheriting the trigger's former Tailwind z-50.
  z-index: 1100 !important;
}

:deep(.v-color-picker-preview__dot) {
  display: none;
}

:deep(.v-color-picker-preview__alpha) {
  display: none;
}

:deep(.v-color-picker.v-sheet) {
  box-shadow: none !important;
  background-color: transparent !important;
  border-radius: 0 !important;
}

:deep(.v-color-picker-preview__sliders) {
  padding-inline-end: 0 !important;
}

:deep(.v-color-picker__controls) {
  padding: 0 0 0 0px !important;
  margin: 0 !important;
}

:deep(.v-slider-thumb) {
  color: rgba(95, 60, 255, 1);
}

:deep(.v-slider-thumb__surface) {
  z-index: 1;
  position: relative;
  width: 12px;
  height: 12px;
  box-shadow: none !important;
}
:deep(.v-slider-thumb__ripple) {
  width: 25px;
  height: 25px;
  background: white;
  border-radius: 100%;
  z-index: 0;
}
:deep(.v-color-picker-canvas__dot) {
  width: 32px !important;
  height: 32px !important;
  background-color: rgba(95, 60, 255, 1) !important;
  border-radius: 100% !important;
  border: none !important;
  box-shadow: none !important;
  border: 8px solid white !important;
}
</style>
